/*
 * The header of the challenge pane, which renders something like
 * "Cover the sun!" in the Parting Clouds challenge.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {staticUrl} from "@khan/urls";

import {
    formatContent,
    stripExtraSpacesFromPre,
} from "./challenge-text-renderer.ts";

type MarkdownRendererProps = {
    // Gimme something to markdown
    markdown: string;
};

const MarkdownRenderer = (props: MarkdownRendererProps) => (
    <span dangerouslySetInnerHTML={{__html: formatContent(props.markdown)}} />
);

type ChallengeStepProps = {
    // An orange reminder message
    alsoMessage?: string;
    // Some kind of alternate message that renders in green
    alternateMessage?: string;
    // Main description
    description: string;
    // Whether or not to display a hint
    hasHint?: boolean;
    // Whether the "what's this?" modal should talk about matching blanks
    hasMatchingBlanks?: boolean;
    // Whether we should avoid rendering hints because of syntax errors
    hasSyntaxErrors?: boolean;
    // The source for a hint image
    image?: string;
    // Whether we're writing processing.js
    isPJS: boolean;
    // Whether we're building a webpage (sounds like fun!)
    isWebpage: boolean;
    // A hint at the structure of the code in this step
    structure?: string;
    // The title of the step we're displaying
    title: string;
};

class ChallengeStep extends React.Component<ChallengeStepProps> {
    renderExplainWebpage: () => Array<React.ReactNode> = () => {
        return [
            <Body tag="p" key="_">
                {t`Underscores "_" mean that a HTML tag name, attribute, or CSS selector, property, or value is expected.`}
            </Body>,
            <Body tag="p" key="...">
                {t`Three dots "..." mean that we expect a value such as a number or text like the contents of a paragraph.`}
            </Body>,
        ];
    };

    renderExplainPJS: () => React.ReactNode = () => {
        return (
            <Body tag="p">
                {t`The empty blanks are parts that you will need to figure out yourself.`}
            </Body>
        );
    };

    renderExplainMatchingBlanks: () => React.ReactNode = () => {
        return (
            <Body tag="p">
                {t`If you see colored blanks, the values you put in two blanks of the same color must be exactly the same.`}
            </Body>
        );
    };

    renderExplainImage: () => React.ReactNode = () => {
        return (
            <Body tag="p">
                {t`The image on the right shows what your program should output, if the code is correct. Some things might be different, like the exact colors or position.`}
            </Body>
        );
    };

    renderExplainModal: () => React.ReactNode = () => {
        const {isWebpage, isPJS, hasMatchingBlanks, image} = this.props;

        const hintDialog = ({closeModal}: {closeModal: () => void}) => (
            <OnePaneDialog
                title={t`What's this?`}
                content={
                    <View>
                        <Body tag="p">
                            {t`This hint shows the code you'll need to successfully complete this step, but it is not the complete answer.`}
                        </Body>
                        {isWebpage && this.renderExplainWebpage()}
                        {isPJS && this.renderExplainPJS()}
                        {hasMatchingBlanks &&
                            this.renderExplainMatchingBlanks()}
                        {image && this.renderExplainImage()}
                    </View>
                }
                footer={<Button onClick={closeModal}>{t`Close`}</Button>}
            />
        );

        return (
            <ModalLauncher modal={hintDialog}>
                {({openModal}) => (
                    <Button
                        kind="tertiary"
                        size="small"
                        onClick={openModal}
                        style={styles.whatsThisHint}
                    >
                        {t`What's this?`}
                    </Button>
                )}
            </ModalLauncher>
        );
    };

    renderStructure: () => React.ReactNode = () => {
        const {hasHint, image, structure} = this.props;
        return (
            <div className={css(styles.structure)}>
                {hasHint && (
                    <h3 className={css(styles.header)}>
                        {t`Hint`}
                        {this.renderExplainModal()}
                    </h3>
                )}
                <div>
                    {image && (
                        <img
                            className={css(styles.exampleImage)}
                            src={staticUrl(image)}
                            alt={t`Screenshot of what your code should output`}
                            title={t`Your code in this step should output something that looks like this`}
                        />
                    )}
                    {structure && (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: stripExtraSpacesFromPre(structure),
                            }}
                        />
                    )}
                </div>
            </div>
        );
    };

    renderDescription: () => React.ReactNode = () => {
        const {description, title} = this.props;
        return (
            <div className={css(styles.description)}>
                <h3 className={css(styles.header)}>{title}</h3>
                <p>
                    <MarkdownRenderer markdown={description} />
                </p>
            </div>
        );
    };

    renderAlsoMessage: () => null | React.ReactElement<
        React.ComponentProps<"span">
    > = () => {
        const {hasSyntaxErrors, alsoMessage} = this.props;
        if (hasSyntaxErrors || !alsoMessage) {
            return null;
        }
        return (
            <span className={css(styles.alsoMessage)}>
                <MarkdownRenderer markdown={alsoMessage} />
            </span>
        );
    };

    renderAlternateMessage: () => null | React.ReactElement<
        React.ComponentProps<"span">
    > = () => {
        const {hasSyntaxErrors, alternateMessage, alsoMessage} = this.props;
        if (hasSyntaxErrors || !alternateMessage || alsoMessage) {
            return null;
        }
        return (
            <span className={css(styles.alternateMessage)}>
                <MarkdownRenderer markdown={alternateMessage} />
            </span>
        );
    };

    render(): React.ReactNode {
        return (
            <div className={css(styles.wrap)}>
                <div className={css(styles.columns)}>
                    {this.renderDescription()}
                    {this.renderStructure()}
                </div>
                <div className={css(styles.message)}>
                    {this.renderAlsoMessage()}
                    {this.renderAlternateMessage()}
                </div>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    alsoMessage: {
        color: "darkorange",
    },
    alternateMessage: {
        color: "green",
    },
    columns: {
        display: "flex",
        width: "100%",
    },
    description: {
        flex: 1,
        maxWidth: 850,
    },
    exampleImage: {
        maxWidth: 140,
    },
    header: {
        marginBottom: 0,
    },
    message: {
        fontWeight: 700,
        fontSize: "1em",
        minHeight: 20,
    },
    structure: {
        // Enforce separation between description and structure
        marginLeft: 20,
    },
    whatsThisHint: {
        marginLeft: "0.6em",
    },
    wrap: {
        width: "100%",
    },
});

export default ChallengeStep;
